import { Icon, Tabs, TabsContent, TabsList, TabsTrigger } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TbListDetails, TbPhoneCall, TbRobot } from "react-icons/tb";
import { z } from "zod";

import { PERMISSIONS } from "@/constants/permissions";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

import { CallsTab } from "./components/calls-tab";
import { TriggerCallTab } from "./components/trigger-call-tab";
import { WorkerTab } from "./components/worker-tab";
import { TELLI_TABS, TELLI_TAB_VALUES } from "./constants/telli.constants";
import { isTelliTab } from "./utils/telli.utils";

const TelliSearchSchema = z.object({
    tab: z.enum(TELLI_TAB_VALUES).optional(),
    contactPage: z.coerce.number().int().min(1).optional(),
    callsPage: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_TELLI)({
    component: TelliPage,
    validateSearch: searchValidator(TelliSearchSchema),
    staticData: {
        config: {
            title: "developer_telli_title",
            breadcrumb: {
                title: "developer_telli_title",
                to: APP_ROUTES_TO.DEVELOPER_TELLI,
            },
        },
        permissions: [PERMISSIONS.CALL_SVC_ADMIN_BASE, PERMISSIONS.CALL_TELLI_MANAGE],
    },
});

function TelliPage() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const search = Route.useSearch();
    const activeTab = search.tab ?? TELLI_TABS.WORKER;

    const handleTabChange = (tab: string) => {
        if (!isTelliTab(tab) || tab === activeTab) return;

        navigate({
            search: (prev) => ({
                ...prev,
                tab,
            }),
            replace: true,
        });
    };

    return (
        <div className="flex flex-col gap-6">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col gap-4">
                <TabsList>
                    <TabsTrigger value={TELLI_TABS.WORKER} className="gap-1.5">
                        <Icon icon={TbRobot} />
                        {t("developer_telli_tab_worker")}
                    </TabsTrigger>
                    <TabsTrigger value={TELLI_TABS.TRIGGER_CALL} className="gap-1.5">
                        <Icon icon={TbPhoneCall} />
                        {t("developer_telli_tab_trigger_call")}
                    </TabsTrigger>
                    <TabsTrigger value={TELLI_TABS.CALLS} className="gap-1.5">
                        <Icon icon={TbListDetails} />
                        {t("developer_telli_tab_calls")}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value={TELLI_TABS.WORKER} className="mt-0">
                    <WorkerTab />
                </TabsContent>

                <TabsContent value={TELLI_TABS.TRIGGER_CALL} className="mt-0">
                    <TriggerCallTab />
                </TabsContent>

                <TabsContent value={TELLI_TABS.CALLS} className="mt-0">
                    <CallsTab />
                </TabsContent>
            </Tabs>
        </div>
    );
}
